// @ts-nocheck — shared v2 form uses wide `any`-shaped state
import React, { useEffect, useMemo, useRef } from "react"
import { Plus, Scale, Trash2 } from "lucide-react"
import Accordion from "./Accordion.jsx"
import InputField from "./InputField.jsx"
import { OrderFormCheckbox, OrderFormRadio } from "./EnterKeyControls.jsx"
import { isProcessingSectionComplete } from "./sectionCompletion.js"
import {
    ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED,
} from "./orderFormAccordionConstants.js"
import { focusElement } from "../utils/scrollFocusedElement.js"
import { COUNTRY_JAPAN } from "../utils/countries.js"

/** @param {Record<string, unknown>} processing */
function firstMissingProcessingFieldId(processing) {
    const rows = processing.instructions ?? []
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        if (row.speciesId == null) {
            return `processing-species-${i}`
        }
        if (row.processId == null) {
            return `processing-method-${i}`
        }
    }
    return null
}

function optionProcessIds(opt) {
    return [
        opt?.process_id1,
        opt?.process_id2,
        opt?.process_id3,
        opt?.process_id4,
        opt?.process_id5,
    ]
        .map((x) => (x == null ? null : Number(x)))
        .filter((x) => Number.isFinite(x))
}

function formatProcessNameList(names) {
    const uniq = Array.from(new Set((names ?? []).map((n) => String(n).trim()).filter(Boolean)))
    if (uniq.length === 0) return ""
    if (uniq.length === 1) return uniq[0]
    if (uniq.length === 2) return `${uniq[0]} and ${uniq[1]}`
    return `${uniq.slice(0, -1).join(", ")}, and ${uniq[uniq.length - 1]}`
}

function formatOptionLabel(processNames, option) {
    const prefix = formatProcessNameList(processNames)
    const d1 = String(option?.description1 ?? "").trim()
    const d2 = String(option?.description2 ?? "").trim()
    const main = d2 ? `${d1} (${d2})` : d1
    return { processLabel: prefix, optionLabel: main }
}

function selectedOptionShortNameListForProcessId(processId, optionsDataset, selectedById) {
    const pid = processId == null ? null : Number(processId)
    if (!Number.isFinite(pid)) return []

    const selected = selectedById ?? {}
    const out = []
    for (const opt of optionsDataset ?? []) {
        const oid = opt?.id
        const optionId = oid == null ? null : Number(oid)
        if (!Number.isFinite(optionId)) continue
        if (!selected[optionId]) continue
        if (!optionProcessIds(opt).includes(pid)) continue
        const sn = String(
            opt?.short_name ?? opt?.shortName ?? opt?.ShortName ?? ""
        ).trim()
        if (sn) out.push(sn)
    }
    return out
}

/**
 * @param {{
 *   value: Record<string, unknown>,
 *   speciesOptions: Array<{ id: unknown, name: string }>,
 *   processes: Array<{ id: unknown, name: string, frozen?: boolean }>,
 *   optionsDataset: Array<any>,
 *   shippingCountry?: string,
 *   onChange: (v: Record<string, unknown>) => void,
 *   onNext: () => void,
 *   title?: string,
 *   isOpen?: boolean,
 *   touched?: boolean,
 *   holdForPickup?: boolean,
 *   statusLabel?: string | null,
 *   disabled?: boolean,
 *   onHeaderClick?: () => void,
 *   isActive?: boolean,
 *   activationFocusMode?: "first" | "nextRequired" | null,
 *   onActivationFocusConsumed?: () => void,
 *   activationFocusDelayMs?: number,
 *   fieldErrors?: Record<string, string> | null,
 *   showFieldErrors?: boolean,
 *   hasErrors?: boolean,
 * }} props
 */
export default function ProcessingSection({
    value,
    speciesOptions,
    processes,
    optionsDataset,
    shippingCountry = "",
    onChange,
    onNext,
    title = "5. Processing Details",
    isOpen,
    touched = false,
    holdForPickup = false,
    statusLabel = null,
    disabled = false,
    onHeaderClick,
    isActive = false,
    activationFocusMode = null,
    onActivationFocusConsumed,
    activationFocusDelayMs = 350,
    fieldErrors = null,
    showFieldErrors = false,
    hasErrors = false,
}) {
    const nextButtonRef = useRef(/** @type {HTMLButtonElement | null} */(null))
    const valueRef = useRef(value)
    valueRef.current = value

    const isCompleted =
        isProcessingSectionComplete(value, { holdForPickup: Boolean(holdForPickup) }) &&
        Boolean(touched)

    const processDropdownOptions = useMemo(() => {
        const isJapan = String(shippingCountry ?? "") === COUNTRY_JAPAN
        if (!isJapan) return processes ?? []

        const selectedProcessIds = new Set(
            (value.instructions ?? [])
                .map((i) => (i?.processId == null ? null : Number(i.processId)))
                .filter((id) => Number.isFinite(id))
        )

        const selectedHasFrozen = (processes ?? []).some((p) => {
            const pid = p?.id == null ? null : Number(p.id)
            if (!Number.isFinite(pid)) return false
            if (!selectedProcessIds.has(pid)) return false
            return Boolean(p?.frozen)
        })

        // If an invalid (frozen) process is already selected, keep the full list visible
        // so the user can see and change the selection.
        if (selectedHasFrozen) return processes ?? []

        return (processes ?? []).filter((p) => !Boolean(p?.frozen))
    }, [processes, shippingCountry, value.instructions])

    const availableOptions = useMemo(() => {
        const selectedProcessIds = new Set(
            (value.instructions ?? [])
                .map((i) => (i?.processId == null ? null : Number(i.processId)))
                .filter((id) => Number.isFinite(id))
        )

        /** @type {Map<number, Set<number>>} optionId -> selectedProcessIdsThatMatch */
        const optionToMatchedProcesses = new Map()

        for (const opt of optionsDataset ?? []) {
            const oid = opt?.id
            const optionId = oid == null ? null : Number(oid)
            if (!Number.isFinite(optionId)) continue

            const pids = optionProcessIds(opt)
            const matched = pids.filter((pid) => selectedProcessIds.has(pid))
            if (matched.length === 0) continue

            const existing = optionToMatchedProcesses.get(optionId) ?? new Set()
            for (const pid of matched) existing.add(pid)
            optionToMatchedProcesses.set(optionId, existing)
        }

        const processNameById = new Map(
            (processes ?? []).map((p) => [Number(p.id), String(p.name ?? "").trim()])
        )

        return Array.from(optionToMatchedProcesses.entries())
            .map(([optionId, matchedProcessIds]) => {
                const opt = (optionsDataset ?? []).find(
                    (o) => Number(o?.id) === optionId
                )
                const names = Array.from(matchedProcessIds)
                    .map((pid) => processNameById.get(Number(pid)) ?? "")
                    .filter(Boolean)
                return {
                    id: optionId,
                    sortProcess1: Number(opt?.process_id1 ?? 0),
                    ...formatOptionLabel(names, opt),
                }
            })
            .sort((a, b) => {
                if (a.sortProcess1 !== b.sortProcess1) {
                    return a.sortProcess1 - b.sortProcess1
                }
                return a.id - b.id
            })
    }, [value.instructions, value.options, processes, optionsDataset])

    useEffect(() => {
        if (
            !isActive ||
            activationFocusMode == null ||
            !onActivationFocusConsumed
        ) {
            return
        }
        const mode = activationFocusMode
        const tmr = window.setTimeout(() => {
            /** @type {HTMLElement | null} */
            let el = null
            if (mode === "first") {
                el = document.getElementById("processing-species-0")
            } else if (mode === "nextRequired") {
                const p = valueRef.current
                if (isProcessingSectionComplete(p, { holdForPickup: Boolean(holdForPickup) })) {
                    el = nextButtonRef.current
                } else {
                    const id = firstMissingProcessingFieldId(p)
                    if (id) {
                        el = document.getElementById(id)
                    } else {
                        el = document.getElementById("processing-total-weight")
                    }
                }
            }
            focusElement(el, { enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED, delay: true })
            onActivationFocusConsumed()
        }, activationFocusDelayMs)
        return () => window.clearTimeout(tmr)
    }, [
        isActive,
        activationFocusMode,
        activationFocusDelayMs,
        onActivationFocusConsumed,
        holdForPickup,
    ])

    function updateInstruction(index, field, fieldValue) {
        const updated = value.instructions.map((instruction, i) => {
            if (i !== index) {
                return instruction
            }
            return { ...instruction, [field]: fieldValue }
        })
        onChange({ ...value, instructions: updated })
    }

    function parseWholeNumberOrEmpty(raw) {
        const trimmed = String(raw ?? "")
        if (trimmed.trim() === "") {
            return ""
        }
        const parsed = Number(trimmed)
        if (!Number.isFinite(parsed)) {
            return ""
        }
        return Math.max(0, Math.trunc(parsed))
    }

    function addInstruction() {
        onChange({
            ...value,
            instructions: [
                ...value.instructions,
                {
                    speciesId: null,
                    processId: null,
                    quantity: 0,
                    unit: "Whole Fish",
                    additionalNotes: "",
                },
            ],
        })
    }

    function removeInstruction(index) {
        onChange({
            ...value,
            instructions: value.instructions.filter((_, i) => i !== index),
        })
    }

    return (
        <Accordion
            title={title}
            isActive={isOpen ?? isActive}
            isCompleted={isCompleted}
            statusLabel={statusLabel}
            disabled={disabled}
            onClick={onHeaderClick}
            hasErrors={hasErrors}
        >
            <div className="row g-4">
            <div className="col-12">
                <div
                    className="p-3 rounded d-flex align-items-start"
                    style={{ backgroundColor: "#e7f3ff", gap: "12px" }}
                >
                    <Scale className="text-primary mt-1" size={20} />
                    <div>
                        <h3
                            className="fw-semibold"
                            style={{ color: "#1e3a8a", fontSize: "1rem", marginBottom: "4px" }}
                        >
                            Processing Details
                        </h3>
                        <p className="mb-0" style={{ color: "#1e40af", fontSize: "0.875rem" }}>
                            Please provide details about your fish processing requirements.
                        </p>
                    </div>
                </div>
            </div>

            <div className="col-12">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h4 className="h5 mb-0">Processing Instructions</h4>
                </div>
                {showFieldErrors && fieldErrors?.instructions ? (
                    <div className="text-danger small mb-3">{fieldErrors.instructions}</div>
                ) : null}

                {value.instructions.map((instruction, index) => (
                    <div key={index} className="p-3 border rounded mb-3">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <h5 className="small fw-semibold text-muted">Process #{index + 1}</h5>
                            {value.instructions.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeInstruction(index)}
                                    className="btn btn-link text-danger p-0"
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>
                        {showFieldErrors && fieldErrors?.[`instructions.${index}.duplicate`] ? (
                            <div className="text-danger small mb-3">{fieldErrors[`instructions.${index}.duplicate`]}</div>
                        ) : null}

                        <div className="row g-3">
                            <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">
                                    Species<span className="text-danger ms-1">*</span>
                                </label>
                                <select
                                    id={`processing-species-${index}`}
                                    value={instruction.speciesId ?? ""}
                                    onChange={(e) => {
                                        const raw = e.target.value
                                        updateInstruction(
                                            index,
                                            "speciesId",
                                            raw === "" ? null : Number(raw)
                                        )
                                    }}
                                    className={`form-select${showFieldErrors && fieldErrors?.[`instructions.${index}.speciesId`] ? " is-invalid" : ""}`}
                                >
                                    <option value="">Select species</option>
                                    {speciesOptions.map((species) => (
                                        <option key={species.id} value={String(species.id)}>
                                            {species.name}
                                        </option>
                                    ))}
                                </select>
                                {showFieldErrors && fieldErrors?.[`instructions.${index}.speciesId`] ? (
                                    <div className="text-danger small mt-1">{fieldErrors[`instructions.${index}.speciesId`]}</div>
                                ) : null}
                            </div>
                            <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">
                                    Processing Method<span className="text-danger ms-1">*</span>
                                </label>
                                <select
                                    id={`processing-method-${index}`}
                                    value={instruction.processId ?? ""}
                                    onChange={(e) => {
                                        const raw = e.target.value
                                        updateInstruction(
                                            index,
                                            "processId",
                                            raw === "" ? null : Number(raw)
                                        )
                                    }}
                                    className={`form-select${showFieldErrors && fieldErrors?.[`instructions.${index}.processId`] ? " is-invalid" : ""}`}
                                >
                                    <option value="">Select method</option>
                                    {processDropdownOptions.map((process) => (
                                        <option key={process.id} value={String(process.id)}>
                                            {process.name}
                                        </option>
                                    ))}
                                </select>
                                {showFieldErrors && fieldErrors?.[`instructions.${index}.processId`] ? (
                                    <div className="text-danger small mt-1">{fieldErrors[`instructions.${index}.processId`]}</div>
                                ) : null}
                            </div>

                            <div className="col-12 col-md-6">
                                <InputField
                                    id={`processing-quantity-${index}`}
                                    label="Quantity"
                                    type="number"
                                    value={instruction.quantity || ""}
                                    error={showFieldErrors ? fieldErrors?.[`instructions.${index}.quantity`] : undefined}
                                    onChange={(e) => {
                                        updateInstruction(
                                            index,
                                            "quantity",
                                            parseWholeNumberOrEmpty(e.target.value)
                                        )
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === ".") {
                                            e.preventDefault()
                                        }
                                    }}
                                    inputMode="numeric"
                                    step="1"
                                />
                            </div>
                            <div className="col-12 col-md-6 mb-3" id={`processing-unit-${index}`}>
                                <label className="form-label">
                                    Unit<span className="text-danger ms-1">*</span>
                                </label>
                                <div className="d-flex gap-3">
                                    {["Whole Fish", "Individual Pieces"].map((unit) => (
                                        <div key={unit} className="form-check">
                                            <OrderFormRadio
                                                className={`form-check-input${showFieldErrors && fieldErrors?.[`instructions.${index}.unit`] ? " is-invalid" : ""}`}
                                                checked={instruction.unit === unit}
                                                onSelect={() =>
                                                    updateInstruction(index, "unit", unit)
                                                }
                                            />
                                            <label className="form-check-label">{unit}</label>
                                        </div>
                                    ))}
                                </div>
                                {showFieldErrors && fieldErrors?.[`instructions.${index}.unit`] ? (
                                    <div className="text-danger small mt-1">{fieldErrors[`instructions.${index}.unit`]}</div>
                                ) : null}
                            </div>

                            <div className="col-12 mb-3">
                                <label className="form-label" htmlFor={`processing-notes-${index}`}>Additional Notes</label>
                                <textarea
                                    id={`processing-notes-${index}`}
                                    rows={3}
                                    className={`form-control${showFieldErrors && fieldErrors?.[`instructions.${index}.additionalNotes`] ? " is-invalid" : ""}`}
                                    value={instruction.additionalNotes}
                                    onChange={(e) => {
                                        updateInstruction(
                                            index,
                                            "additionalNotes",
                                            e.target.value
                                        )
                                    }}
                                />
                                {showFieldErrors && fieldErrors?.[`instructions.${index}.additionalNotes`] ? (
                                    <div className="text-danger small mt-1">{fieldErrors[`instructions.${index}.additionalNotes`]}</div>
                                ) : null}
                            </div>

                            <div className="col-12">
                                <button
                                    type="button"
                                    id={index === value.instructions.length - 1 ? "processing-add-instruction" : undefined}
                                    onClick={addInstruction}
                                    className="btn btn-link text-primary p-0 d-flex align-items-center"
                                    style={{ textDecoration: "none" }}
                                >
                                    <Plus size={16} className="me-1" />
                                    <span>Add Another Process</span>
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {value.instructions.length === 0 ? (
                    <button
                        type="button"
                        id="processing-add-instruction"
                        onClick={addInstruction}
                        className="btn btn-link text-primary p-0 d-flex align-items-center"
                        style={{ textDecoration: "none" }}
                    >
                        <Plus size={16} className="me-1" />
                        <span>Add Another Process</span>
                    </button>
                ) : null}
            </div>

            {availableOptions.length > 0 && (
                <div className="col-12" id="processing-options">
                    <div className="border-top pt-3">
                        <h4 className="h5 mb-3">Additional Processing Options</h4>
                        <div className="row g-2">
                            {availableOptions.map((option) => (
                                <div key={option.id} className="col-12">
                                    <div className="form-check">
                                        <OrderFormCheckbox
                                            className={`form-check-input border-secondary${showFieldErrors && fieldErrors?.[`options.${option.id}`] ? " is-invalid" : ""}`}
                                            checked={Boolean(value.options?.[option.id])}
                                            onCheckedChange={(checked) => {
                                                onChange({
                                                    ...value,
                                                    options: {
                                                        ...value.options,
                                                        [option.id]: Boolean(checked),
                                                    },
                                                })
                                            }}
                                        />
                                        <label className="form-check-label">
                                            {option.processLabel ? (
                                                <>
                                                    <span className="fw-semibold">
                                                        {option.processLabel}
                                                    </span>{" "}
                                                    — {option.optionLabel}
                                                </>
                                            ) : (
                                                option.optionLabel
                                            )}
                                        </label>
                                        {showFieldErrors && fieldErrors?.[`options.${option.id}`] ? (
                                            <div className="text-danger small mt-1">{fieldErrors[`options.${option.id}`]}</div>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="col-12 col-md-6">
                <InputField
                    id="processing-total-weight"
                    label="Total Weight of All Fish (lbs)"
                    type="number"
                    value={value.totalWeight}
                    error={showFieldErrors ? fieldErrors?.totalWeight : undefined}
                    onChange={(e) => onChange({ ...value, totalWeight: e.target.value })}
                />
            </div>
            <div className="col-12 col-md-6">
                <InputField
                    id="processing-containers"
                    label="Number of Containers/Boxes"
                    type="number"
                    min="0"
                    value={value.containers}
                    error={showFieldErrors ? fieldErrors?.containers : undefined}
                    onKeyDown={(e) => {
                        if (e.key === "-") {
                            e.preventDefault()
                        }
                    }}
                    onChange={(e) => {
                        const raw = e.target.value
                        if (raw === "") {
                            onChange({ ...value, containers: "" })
                            return
                        }
                        const n = Number(raw)
                        onChange({
                            ...value,
                            containers: Number.isFinite(n) ? String(Math.max(0, n)) : "",
                        })
                    }}
                />
            </div>

            <div className="col-12">
                <div className="p-3 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                    <h4 className="h5 mb-3">Processing Summary</h4>
                    <div className="row g-2">
                        {value.instructions.map((instruction, i) => (
                            <div key={i} className="col-12">
                                <div className="small text-muted mb-1">
                                    {(() => {
                                        const pid =
                                            instruction.processId == null ?
                                                null :
                                                Number(instruction.processId)
                                        const method = String(
                                            (processes ?? []).find(
                                                (p) => String(p?.id) === String(instruction.processId)
                                            )?.name ?? ""
                                        ).trim()
                                        const species = String(
                                            (speciesOptions ?? []).find(
                                                (s) => String(s?.id) === String(instruction.speciesId)
                                            )?.name ?? ""
                                        ).trim()
                                        const shortNames =
                                            selectedOptionShortNameListForProcessId(
                                                pid,
                                                optionsDataset,
                                                value.options
                                            )

                                        return (
                                            <>
                                                Process #{i + 1}:
                                                {species && ` ${species} –`}
                                                {method && ` ${method}`}
                                                {shortNames.length > 0 &&
                                                    ` (${shortNames.join("/")})`}
                                                {method && " –"}
                                            </>
                                        )
                                    })()}
                                    {instruction.quantity > 0 &&
                                        ` ${instruction.quantity} ${instruction.unit}`}
                                </div>
                                {String(instruction.additionalNotes ?? "").trim() !== "" && (
                                    <div className="small fst-italic ms-3" style={{ color: "#495057" }}>
                                        Notes: {String(instruction.additionalNotes).trim()}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="col-12">
                <div className="d-flex justify-content-end">
                    <button
                        ref={nextButtonRef}
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={onNext}
                    >
                        Next
                    </button>
                </div>
            </div>
            </div>
        </Accordion>
    )
}
